import { Box, Typography, Button, Card, CardContent, Chip, Link, Tooltip, Alert, Divider } from '@mui/material';
import { useSubscriptionStatus, useBillingPortal } from '../../hooks/useCommon';
import { useAuth } from '../../hooks/useAuth';
import { getPlanLifecycle, formatBillingDate } from '../../lib/utils';
import { useTranslation } from 'react-i18next';

interface PlanStatusCardProps {
  currentPlan: any;
  usage: any;
  onNavigate: (path: string) => void;
}

const PlanStatusCard: React.FC<PlanStatusCardProps> = ({ currentPlan, usage, onNavigate }) => {
  const { t } = useTranslation('dashboard');
  const { user } = useAuth();
  const { data: subscriptionStatus } = useSubscriptionStatus(user?.id);
  const billingPortal = useBillingPortal();
  
  const usageCount = usage?.generations_used || (usage as any)?.content_generated || 0;
  const usageLimit = currentPlan?.monthly_limit || 1000;
  const usagePercentage = usageLimit > 0 ? (usageCount / usageLimit) * 100 : 0;
  const isNearLimit = usagePercentage >= 80;
  
  // Daily limits
  const dailyUsed = usage?.daily_used || 0;
  const dailyLimit = usage?.daily_limit || currentPlan?.daily_limit || 0;
  const dailyPercentage = dailyLimit > 0 ? (dailyUsed / dailyLimit) * 100 : 0;
  const isNearDailyLimit = dailyPercentage >= 80;

  // Image generation limits
  const imageMonthlyUsed = usage?.image_monthly_used || 0;
  const imageMonthlyLimit = currentPlan?.image_monthly_limit_images ?? null;
  const imageEnabled = currentPlan?.image_generation_enabled ?? false;
  const imagePercentage = imageMonthlyLimit && imageMonthlyLimit > 0 ? (imageMonthlyUsed / imageMonthlyLimit) * 100 : 0;
  const isNearImageLimit = imagePercentage >= 80;

  // DEBUG — remove after confirming
  console.log('[PlanStatusCard] currentPlan:', currentPlan);
  console.log('[PlanStatusCard] usage:', usage);
  console.log('[PlanStatusCard] imageEnabled:', imageEnabled, '| imageMonthlyLimit:', imageMonthlyLimit, '| imageMonthlyUsed:', imageMonthlyUsed);
  console.log('[PlanStatusCard] will render image section?', imageEnabled && imageMonthlyLimit !== null && imageMonthlyLimit > 0);
  
  const isFreePlan = !currentPlan || 
    currentPlan.name === 'Free' || 
    currentPlan.name === 'Free Plan' || 
    currentPlan.name === 'FreeTest';
  
  const hasActiveSubscription = subscriptionStatus && 
    (subscriptionStatus.status === 'active' || subscriptionStatus.status === 'trialing' || subscriptionStatus.status === 'past_due');
  
  const lifecycle = getPlanLifecycle(subscriptionStatus);
  
  const getStatusChipColor = (status: string) => {
    const statusLower = status.toLowerCase();
    if (statusLower === 'canceling') return 'error';
    if (statusLower === 'active') return 'success';
    if (statusLower === 'trialing') return 'info';
    if (statusLower === 'past_due') return 'warning';
    if (statusLower === 'canceled') return 'error';
    return 'default';
  };
  
  const getStatusLabel = (status: string) => {
    const statusLower = status.toLowerCase();
    if (statusLower === 'canceling') return t('plan.canceling');
    if (statusLower === 'active') return t('plan.active');
    if (statusLower === 'trialing') return t('plan.trialing');
    if (statusLower === 'past_due') return t('plan.pastDue');
    if (statusLower === 'canceled') return t('plan.canceled');
    return status;
  };
  
  const handleManagePlan = async () => {
    try {
      await billingPortal.mutateAsync();
    } catch (error) {
      // Error handled by mutation
    }
  };

  return (
    <Card sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      transition: 'all 0.2s ease',
      '&:hover': {
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      },
    }}>
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            {t('plan.title')}
          </Typography>
        </Box>
        
        <Box sx={{ textAlign: 'center', py: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Typography variant="h4" color="primary.main" sx={{ fontWeight: 700 }}>
              {currentPlan?.name || t('plan.freePlan')}
            </Typography>
            {hasActiveSubscription && (
              <Chip 
                label={lifecycle.isCanceling ? t('plan.canceling') : getStatusLabel(lifecycle.statusLabel)}
                color={getStatusChipColor(lifecycle.isCanceling ? 'canceling' : lifecycle.statusLabel)}
                size="small"
              />
            )}
          </Box>
          
          {/* Date information - don't show for past_due */}
          {hasActiveSubscription && subscriptionStatus?.status !== 'past_due' && (
            <Typography 
              variant="body2" 
              sx={{ 
                mb: 2,
                fontWeight: 500,
                background: 'linear-gradient(135deg, #8b9cff 0%, #ff6b9d 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              {lifecycle.isCanceling 
                ? t('plan.cancelsOn', { date: formatBillingDate(lifecycle.cancelsOn) })
                : lifecycle.renewsOn 
                ? t('plan.renewsOn', { date: formatBillingDate(lifecycle.renewsOn) })
                : null}
            </Typography>
          )}
          
          {/* Payment failure banner - show when past_due */}
          {hasActiveSubscription && subscriptionStatus?.status === 'past_due' && (
            <Alert severity="error" sx={{ mt: 1, mb: 2 }}>
              {t('plan.paymentFailed')}
            </Alert>
          )}
          {/* Monthly Usage */}
          {usageLimit > 0 && usageLimit < 10000 ? (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {usageCount} / {usageLimit} {t('plan.thisMonth')} · {Math.round(usagePercentage)}% {t('plan.used')}
              </Typography>
              <Box 
                sx={{ 
                  height: 8, 
                  borderRadius: 1, 
                  backgroundColor: 'grey.200',
                  overflow: 'hidden'
                }}
              >
                <Box 
                  sx={{ 
                    height: '100%', 
                    width: `${Math.min(usagePercentage, 100)}%`,
                    backgroundColor: isNearLimit ? 'warning.main' : 'success.main',
                    transition: 'width 0.3s ease'
                  }} 
                />
              </Box>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {usageCount} / {usageLimit === Infinity || usageLimit > 9999 ? '∞' : usageLimit} {t('plan.thisMonth')}
            </Typography>
          )}
          
          {/* Daily Usage */}
          {dailyLimit > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {dailyUsed} / {dailyLimit} {t('plan.today')} · {Math.round(dailyPercentage)}% {t('plan.used')}
              </Typography>
              <Box sx={{ height: 8, borderRadius: 1, backgroundColor: 'grey.200', overflow: 'hidden' }}>
                <Box sx={{ height: '100%', width: `${Math.min(dailyPercentage, 100)}%`, backgroundColor: isNearDailyLimit ? 'warning.main' : 'info.main', transition: 'width 0.3s ease' }} />
              </Box>
            </Box>
          )}

          {/* AI Image Generation Usage */}
          {imageEnabled && imageMonthlyLimit !== null && imageMonthlyLimit > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box>
                <Typography variant="body2" fontWeight={500} color="text.secondary" sx={{ mb: 1 }}>
                  🖼 {t('plan.imageGenerations', 'AI Images')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {imageMonthlyUsed} / {imageMonthlyLimit} {t('plan.thisMonth')} · {Math.round(imagePercentage)}% {t('plan.used')}
                </Typography>
                <Box sx={{ height: 8, borderRadius: 1, backgroundColor: 'grey.200', overflow: 'hidden' }}>
                  <Box sx={{ height: '100%', width: `${Math.min(imagePercentage, 100)}%`, backgroundColor: isNearImageLimit ? 'warning.main' : 'secondary.main', transition: 'width 0.3s ease' }} />
                </Box>
              </Box>
            </>
          )}

          <Box sx={{ mt: 'auto', pt: 1.5 }}>
            {isFreePlan ? (
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => onNavigate('/billing')}
                  sx={{ 
                    minWidth: 120,
                    textTransform: 'none',
                    background: 'linear-gradient(135deg, #8b9cff 0%, #ff6b9d 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5a6fd8 0%, #d94f7e 100%)',
                    },
                  }}
                >
                  {t('plan.upgrade')}
                </Button>
              </Box>
            ) : hasActiveSubscription ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleManagePlan}
                  disabled={billingPortal.isPending}
                  sx={{ 
                    minWidth: 120,
                    textTransform: 'none',
                    background: 'linear-gradient(135deg, #8b9cff 0%, #ff6b9d 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5a6fd8 0%, #d94f7e 100%)',
                    },
                  }}
                >
                  {billingPortal.isPending ? t('plan.opening') : t('plan.manage')}
                </Button>
                {lifecycle.isCanceling && (
                  <Tooltip title={t('plan.reactivate')}>
                    <Link
                      component="button"
                      variant="body2"
                      onClick={handleManagePlan}
                      sx={{ 
                        cursor: 'pointer',
                        textDecoration: 'none',
                        color: 'primary.main',
                        '&:hover': { textDecoration: 'underline' }
                      }}
                    >
                      {t('plan.reactivate')}
                    </Link>
                  </Tooltip>
                )}
              </Box>
            ) : null}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default PlanStatusCard;
